import type { StreamEntry } from "./history/types.js";
import { bodyOfChunks, chunksOf, withTextBody } from "./history/rows.js";

function replacePieces(pieces: string[], from: string, to: string): string[] | undefined {
  const joined = pieces.join("");
  const matches = [
    ...joined.matchAll(new RegExp(from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")),
  ];
  if (matches.length === 0) return;
  const out = [...pieces];
  let offset = 0;
  const starts = pieces.map((piece) => {
    const start = offset;
    offset += piece.length;
    return start;
  });
  for (const match of matches.toReversed()) {
    const start = match.index;
    const end = start + from.length;
    const first = starts.findLastIndex((pieceStart) => pieceStart <= start);
    const last = starts.findLastIndex((pieceStart) => pieceStart < end);
    if (first === last)
      out[first] =
        out[first].slice(0, start - starts[first]) + to + out[first].slice(end - starts[first]);
    else {
      out[first] = out[first].slice(0, start - starts[first]) + to;
      for (let i = first + 1; i < last; i += 1) out[i] = "";
      out[last] = out[last].slice(end - starts[last]);
    }
  }
  return out;
}

// A patch naming its reply finds it anywhere: the upload can land after the next prompt.
// One without patches only the latest reply; older turns and other agents keep their output.
export function rewriteLiveOutputEntries(
  entries: readonly StreamEntry[],
  from: string,
  to: string,
  messageId?: string,
): StreamEntry[] | undefined {
  if (typeof from !== "string" || typeof to !== "string" || !from || from === to) return;
  const end = entries.at(-1)?.kind === "turn_boundary" ? entries.length - 1 : entries.length;
  const start =
    messageId === undefined
      ? entries
          .slice(0, end)
          .findLastIndex(
            (entry) => entry.kind === "user_message" || entry.kind === "turn_boundary",
          ) + 1
      : 0;
  const sameReply = (entry: StreamEntry) =>
    entry.kind === "text" && (messageId === undefined || entry.messageId === messageId);
  const next = [...entries];
  let changed = false;
  for (let at = start; at < end; ) {
    if (!sameReply(next[at])) {
      at += 1;
      continue;
    }
    let stop = at;
    while (stop < end && sameReply(next[stop])) stop += 1;
    const run = next
      .slice(at, stop)
      .filter((entry): entry is StreamEntry & { kind: "text" } => entry.kind === "text");
    const rowParts = run.map(chunksOf);
    const rewritten = replacePieces(
      rowParts.flat().map((chunk) => chunk.text),
      from,
      to,
    );
    if (rewritten) {
      let piece = 0;
      run.forEach((entry, index) => {
        const body = rowParts[index].map((chunk) =>
          Object.assign({}, chunk, { text: rewritten[piece++] }),
        );
        next[at + index] = withTextBody(entry, bodyOfChunks(body));
      });
      changed = true;
    }
    at = stop;
  }
  return changed ? next : undefined;
}
